import { v4 as uuidv4 } from 'uuid';
import { normalizeHexColor } from './richText.js';
import { normalizeStoredImageItem, revokeLocalImageUrl } from './localImage.js';
import { LOCAL_ASSET } from './localAssets.js';
import {
	formatAudioDuration,
	normalizeLocalAudioItem,
	revokeLocalAudioItem,
} from './localAudio.js';

import { getMediaItemUrl } from './qiniuMedia.js';

export const AUDIO_LIBRARY_TYPE = 'audio-library';
export const AUDIO_LIBRARY_DEFAULT_COVER_SIZE = 100;
export const AUDIO_LIBRARY_DEFAULT_CARD_RADIUS = 12;
export const AUDIO_LIBRARY_DEFAULT_COVER_RADIUS = 8;
export const AUDIO_LIBRARY_CARD_PADDING = 12;
export const AUDIO_LIBRARY_COL_GAP = 12;
export const AUDIO_LIBRARY_COL_COUNT = 3;

const DEFAULT_ACCENT_COLOR = '#52c41a';
const DEFAULT_TITLE_COLOR = '#333333';
const DEFAULT_NAME_COLOR = '#333333';

// 默认音频文件的字节数（占位值，待替换为 audio-lib-1/2/3.mp3 实际大小）
const AUDIO_LIBRARY_DEFAULT_SIZE = {
	1: Math.round(2.34 * 1024 * 1024), 
	2: Math.round(3.81 * 1024 * 1024), 
	3: Math.round(2.84 * 1024 * 1024), 
};
const AUDIO_LIBRARY_DEFAULT_SIZE_LABEL = {
	1: '2.34 MB',
	2: '3.81 MB',
	3: '2.84 MB',
};

const ITEM_PRESETS = [
	{ id: 'da-41001', filename: '安静空灵.mp3' },
	{ id: 'da-41002', filename: '雨天放松助眠.mp3' },
	{ id: 'da-41003', filename: '美丽自然.mp3' },
];

const LOCAL_COVERS = LOCAL_ASSET.audioLibrary;
const PRESETS = LOCAL_ASSET.audioLibraryPresets || [];

function createDefaultCover(preset = {}, index = 0) {
	const filename = preset?.coverFilename || 'cover.jpg';
	const coverUrl = preset?.coverUrl || LOCAL_COVERS[index % LOCAL_COVERS.length];
	return {
		id: `audio-library-cover-${uuidv4()}`,
		filename,
		previewUrl: coverUrl,
		url: '',
		local: false,
	};
}

function createDefaultAudio(preset = {}, index = 0) {
	const audioUrl = preset?.audioUrl || '';
	const hasPreset = Boolean(audioUrl || preset?.audio || preset?.filesize_byte !== undefined);
	const presetIndex = index + 1;
	return {
		// 优先使用 preset 显式声明的 id，否则回退到 uuid（用户上传时）
		id: preset?.id || `audio-library-audio-${uuidv4()}`,
		filename: preset?.filename || '音频.mp3',
		// 优先读 preset 中的 filesize（外部预设可能有独立大小），否则用默认映射
		filesize: preset?.filesize || (hasPreset ? AUDIO_LIBRARY_DEFAULT_SIZE_LABEL[presetIndex] || '' : ''),
		filesize_byte: preset?.filesize_byte ?? (hasPreset ? AUDIO_LIBRARY_DEFAULT_SIZE[presetIndex] || 0 : 0),
		previewUrl: audioUrl,
		duration: preset?.duration ?? 0,
		durationText: formatAudioDuration(preset?.duration ?? 0),
		url: audioUrl,
		local: false,
	};
}

export function createDefaultAudioLibraryItem(preset = {}, index = 0) {
	const safePreset = PRESETS[index] || {};
	const mergedPreset = { ...safePreset, ...preset };
	return {
		id: preset.id || safePreset.id || uuidv4(),
		cover: createDefaultCover(mergedPreset, index),
		audio: createDefaultAudio(mergedPreset, index),
	};
}

export function createDefaultAudioLibraryItems(count = 3) {
	const items = [];
	for (let i = 0; i < count; i += 1) {
		items.push(createDefaultAudioLibraryItem(ITEM_PRESETS[i] || {}, i));
	}
	return items;
}

export function getAudioLibraryDefaults() {
	return normalizeAudioLibraryData({
		title: '我的音频',
		items: createDefaultAudioLibraryItems(3),
		showCard: false,
	});
}

export function isAudioLibraryBlock(blockType) {
	return blockType === AUDIO_LIBRARY_TYPE;
}

export function getAudioLibraryCoverUrl(item) {
	if (!item?.cover) return '';
	return getMediaItemUrl(item.cover);
}

export function getAudioLibraryAudioUrl(item) {
	if (!item?.audio) return '';
	return getMediaItemUrl(item.audio);
}

export function getAudioLibraryItemName(item) {
	return item?.audio?.filename || '音频.mp3';
}

function normalizeCoverImage(image, fallback) {
	if (!image?.previewUrl && !image?.url) {
		return fallback ? { ...fallback } : createDefaultCover();
	}
	return normalizeStoredImageItem(image, fallback);
}

function normalizeAudioAsset(audio, fallback) {
	if (!audio?.previewUrl && !audio?.url && !audio?.filename) {
		return fallback ? { ...fallback } : createDefaultAudio();
	}
	return normalizeLocalAudioItem({
		...fallback,
		...audio,
	});
}

export function normalizeAudioLibraryItem(item = {}, index = 0) {
	const fallback = createDefaultAudioLibraryItem(ITEM_PRESETS[index] || {}, index);
	return {
		id: item.id || uuidv4(),
		cover: normalizeCoverImage(item.cover, fallback.cover),
		audio: normalizeAudioAsset(item.audio, fallback.audio),
	};
}

export function normalizeAudioLibraryItems(items = []) {
	if (!items.length) {
		return createDefaultAudioLibraryItems(3);
	}
	return items.map((item, index) => normalizeAudioLibraryItem(item, index));
}

export function normalizeAudioLibraryData(data = {}) {
	const coverSize = Number(data.coverSize);
	return {
		title: data.title ?? '我的音频',
		accentColor: normalizeHexColor(data.accentColor || DEFAULT_ACCENT_COLOR),
		titleColor: normalizeHexColor(data.titleColor || DEFAULT_TITLE_COLOR),
		nameColor: normalizeHexColor(data.nameColor || DEFAULT_NAME_COLOR),
		items: normalizeAudioLibraryItems(data.items),
		showCard: data.showCard === true,
		cardBorderRadius: Number.isFinite(Number(data.cardBorderRadius))
			? Math.max(0, Number(data.cardBorderRadius))
			: AUDIO_LIBRARY_DEFAULT_CARD_RADIUS,
		coverBorderRadius: Number.isFinite(Number(data.coverBorderRadius))
			? Math.max(0, Number(data.coverBorderRadius))
			: AUDIO_LIBRARY_DEFAULT_COVER_RADIUS,
		coverSize: Number.isFinite(coverSize) && coverSize > 0
			? coverSize
			: AUDIO_LIBRARY_DEFAULT_COVER_SIZE,
		coverResponsive: data.coverResponsive !== false,
	};
}

export function getAudioLibraryCoverStyle(data = {}, { h5 = false, fixed } = {}) {
	const normalized = normalizeAudioLibraryData(data);
	const pxRatio = h5 ? 50 : 1;
	const radius = normalized.coverBorderRadius;
	const borderRadius = h5 ? `${radius / pxRatio}rem` : `${radius}px`;
	const useFixed = fixed ?? !normalized.coverResponsive;

	if (useFixed) {
		const size = normalized.coverSize;
		const sizeStr = h5 ? `${size / pxRatio}rem` : `${size}px`;
		return {
			width: sizeStr,
			height: sizeStr,
			margin: '0 auto',
			borderRadius,
		};
	}

	return {
		width: '100%',
		aspectRatio: '1 / 1',
		borderRadius,
	};
}

export function getAudioLibraryBlockStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizeAudioLibraryData(data);
	const pxRatio = h5 ? 50 : 1;
	const coverRadius = normalized.coverBorderRadius;
	const cardRadius = normalized.cardBorderRadius;
	const coverSize = normalized.coverSize;

	return {
		coverSize: h5 ? `${coverSize / pxRatio}rem` : `${coverSize}px`,
		coverBorderRadius: h5 ? `${coverRadius / pxRatio}rem` : `${coverRadius}px`,
		cardBorderRadius: h5 ? `${cardRadius / pxRatio}rem` : `${cardRadius}px`,
		cardPadding: h5 ? `${AUDIO_LIBRARY_CARD_PADDING / pxRatio}rem` : `${AUDIO_LIBRARY_CARD_PADDING}px`,
		colGap: h5 ? `${AUDIO_LIBRARY_COL_GAP / pxRatio}rem` : `${AUDIO_LIBRARY_COL_GAP}px`,
		showCard: normalized.showCard,
	};
}

export function replaceAudioLibraryCover(currentCover, file, createLocalImageItem) {
	if (currentCover?.local) {
		revokeLocalImageUrl(currentCover.previewUrl);
	}
	return createLocalImageItem(file);
}

export function replaceAudioLibraryAudio(currentAudio, nextAudio) {
	if (currentAudio?.local) {
		revokeLocalAudioItem(currentAudio);
	}
	return normalizeLocalAudioItem(nextAudio);
}

export function revokeAudioLibraryItems(items = []) {
	items.forEach((item) => {
		if (item?.cover?.local) {
			revokeLocalImageUrl(item.cover.previewUrl);
		}
		if (item?.audio?.local) {
			revokeLocalAudioItem(item.audio);
		}
	});
}
